import { CSSProperties, useState } from "react";
import { useCombobox } from "downshift";

export type SelectComboboxProps<T> = {
  selectedItem: T | null;
  setSelectedItem: (item: T | null) => void;
  getItemsFilter: (inputValue: string) => (item: T) => boolean;
  items: T[];
  itemIsSelected: (item: T) => boolean;
  itemToString: (item: T | null) => string;
  placeholder?: string;
  itemKey: keyof T;
  inputClassName?: string;
  inputStyle?: CSSProperties;
  menuClassName?: string;
  menuStyle?: CSSProperties;
  listItemClassName?: string;
  listItemStyle?: CSSProperties;
};

export default function SelectCombobox<T>(props: SelectComboboxProps<T>) {
  const {
    getItemsFilter,
    items: allItems,
    inputClassName,
    inputStyle,
    itemIsSelected,
    itemKey,
    itemToString,
    listItemClassName,
    listItemStyle,
    menuClassName,
    menuStyle,
    placeholder,
    selectedItem,
    setSelectedItem,
  } = props;
  const [inputValue, setInputValue] = useState("");
  const [items, setItems] = useState(allItems);
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    onInputValueChange({ inputValue }) {
      setItems(allItems.filter(getItemsFilter(inputValue)));
    },
    items,
    itemToString,
    onSelectedItemChange(changes) {
      const { selectedItem } = changes;
      setSelectedItem(selectedItem);
      setInputValue(itemToString(selectedItem));
    },
    stateReducer(_state, actionAndChanges) {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
          if (selectedItem) {
            return {
              ...changes,
              inputValue: itemToString(selectedItem),
            };
          }
          return changes;
        case useCombobox.stateChangeTypes.InputChange:
          const clearSelection = !changes.inputValue
            ? { selectedItem: null }
            : {};
          setInputValue(changes.inputValue ?? "");
          return {
            ...changes,
            inputValue: changes.inputValue ?? "",
            ...clearSelection,
          };
        default:
          return changes;
      }
    },
  });

  return (
    <div className="position-relative">
      <input
        placeholder={placeholder ?? "Søg og vælg ..."}
        className={`kommune-combobox-input w-100 ${isOpen ? "is-open" : ""} ${
          inputClassName
        }`}
        style={inputStyle}
        {...getInputProps()}
      />
      <ul
        className={`kommune-combobox-ul  ${!isOpen && "hidden"} ${
          menuClassName
        }`}
        style={menuStyle}
        {...getMenuProps()}
      >
        {isOpen &&
          items.map((item, index) => (
            <li
              className={`filtermenuitem ${
                highlightedIndex === index ? "active " : ""
              } ${itemIsSelected(item) ? "bg-s" : ""} ${listItemStyle} ${listItemClassName}`}
              key={`${item[itemKey]}${index}`}
              style={listItemStyle}
              {...getItemProps({ item, index })}
            >
              <span>{itemToString(item)}</span>
            </li>
          ))}
        {isOpen && items.length === 0 && (
          <li className={`filtermenuitem`}>
            <span>Intet resultat for "{inputValue}"</span>
          </li>
        )}
      </ul>
    </div>
  );
}
